import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Activity,
  Cpu,
  HardDrive,
  MemoryStick,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Server,
  Clock,
  ArrowLeft
} from 'lucide-react';
import AxiosDB from '@/config/axios2';


const SystemStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchStatus();

    if (autoRefresh) {
      const interval = setInterval(fetchStatus, 5000); // Refresh every 5 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const fetchStatus = async () => {
    try {
      // Use axios without auth headers since this is a public endpoint
      const response = await AxiosDB.get(`/telemetria/`, {
        headers: {}, // No auth required
        timeout: 10000
      });

      setStatus(response.data);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (error) {
      console.error('Error fetching system status:', error);
      // Set error state but don't block the UI
      setLoading(false);
    }
  };





  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
      case 'operational':
        return 'bg-green-100 text-green-800';
      case 'stopping':
      case 'stopped':
        return 'bg-red-100 text-red-800';
      case 'launching':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
      case 'operational':
        return <CheckCircle2 className="h-4 w-4" />;
      case 'stopping':
      case 'stopped':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  if (loading && !status) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Cargando estado del sistema...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8" data-testid="system-status-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div>
                <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Estado del Sistema
                </h1>
                <p className="text-gray-600 mt-1">Monitoreo en tiempo real de procesos y recursos</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={autoRefresh ? 'border-green-500 text-green-600' : ''}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
                {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchStatus}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Actualizar
              </Button>
            </div>
          </div>

          {lastUpdate && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Clock className="h-4 w-4" />
              Última actualización: {lastUpdate.toLocaleTimeString('es-ES')}
            </div>
          )}
        </div>

        {/* Overall Status */}
        <Card className="mb-6 shadow-lg">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left side - Status */}
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-full ${status?.status === 'operational' &&
                  status?.processes?.every(p => p.estado === 'online' || p.estado === 'unknown')
                  ? 'bg-green-100'
                  : 'bg-red-100'
                  }`}>
                  <Server className={`h-6 w-6 ${status?.status === 'operational' &&
                    status?.processes?.every(p => p.estado === 'online' || p.estado === 'unknown')
                    ? 'text-green-600'
                    : 'text-red-600'
                    }`} />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Sistema {
                      status?.status === 'operational' &&
                        status?.processes?.every(p => p.estado === 'online' || p.estado === 'unknown')
                        ? 'Operacional'
                        : 'Con Problemas'
                    }
                  </h2>
                  <p className="text-gray-600 mb-3">
                    {status?.processes?.filter(p => p.estado === 'online' || p.estado === 'unknown').length || 0} de{' '}
                    {status?.processes?.length || 0} procesos activos
                  </p>




                </div>
              </div>

              {/* Right side - System Resources */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Recursos del Sistema
                </h3>

                {/* CPU Total */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Cpu className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium">CPU Total PM2</span>
                    </div>
                    <Badge className={
                      (status?.processes?.reduce((sum, p) => sum + (p.cpu || 0), 0) || 0) < 50
                        ? 'bg-green-100 text-green-800'
                        : (status?.processes?.reduce((sum, p) => sum + (p.cpu || 0), 0) || 0) < 80
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                    }>
                      {(status?.processes?.reduce((sum, p) => sum + (parseFloat(p.cpu) || 0), 0) || 0).toFixed(1)}%
                    </Badge>
                  </div>
                  <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                    <div
                      style={{ width: `${Math.min(status?.processes?.reduce((sum, p) => sum + (parseFloat(p.cpu) || 0), 0) || 0, 100)}%` }}
                      className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${(status?.processes?.reduce((sum, p) => sum + (parseFloat(p.cpu) || 0), 0) || 0) < 50
                        ? 'bg-green-500'
                        : (status?.processes?.reduce((sum, p) => sum + (parseFloat(p.cpu) || 0), 0) || 0) < 80
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                        }`}
                    />
                  </div>
                </div>


                {/* Memory Total */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <MemoryStick className="h-4 w-4 text-purple-600" />
                      <span className="text-sm font-medium">Memoria Total PM2</span>
                    </div>
                    <span className="text-sm font-semibold">
                      {status?.system?.memory_percent} %
                    </span>
                  </div>
                  <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                    <div
                      style={{
                        width: `${Math.min(
                          (status?.system?.memory_percent)
                        )}%`
                      }}
                      className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-purple-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Processes */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Procesos PM2
            </CardTitle>
            <CardDescription>
              {status?.telemetry_source === 'external'
                ? 'Datos desde telemetría externa'
                : status?.telemetry_source === 'local'
                  ? 'Datos desde supervisor local'
                  : 'Estado de procesos'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {status?.telemetry_source === 'unavailable' || (status?.processes && status.processes.length === 0 && status?.status === 'error') ? (
              <div className="text-center py-12">
                <XCircle className="h-16 w-16 text-red-300 mx-auto mb-4" />
                <p className="text-xl font-semibold text-red-500">Datos no disponibles</p>
                <p className="text-gray-500 mt-2">No se pudo obtener información de los procesos</p>
              </div>
            ) : status?.processes && status.processes.length > 0 ? (
              <div className="space-y-3">
                {status.processes.map((process, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
                    data-testid={`process-${process.name}`}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <Badge className={`${getStatusColor(process.estado)} flex items-center gap-1`}>
                        {getStatusIcon(process.estado)}
                        {process.estado}
                      </Badge>
                      <div className="flex-1">
                        <p className="font-semibold">{process.name}</p>
                        {/* <p className="text-sm text-gray-500">PID: {process.pid}</p> */}
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-center">
                        <p className="text-gray-500">CPU</p>
                        <p className="font-semibold">{process.cpu}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500">Memoria</p>
                        <p className="font-semibold">{process.Memoria}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500">Uptime</p>
                        <p className="font-semibold">{process.Uptime}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500">Reinicios</p>
                        <p className="font-semibold">{process.reinicios}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No se encontraron procesos PM2
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default SystemStatus;
